import { Acc } from '../../../../components/edit-object/design/Accordion';
import { DynamicTextFields } from '../../../../components/DynamicTextFields';
import { getArtistDisplayName } from '../../../../utils/displayName';
import type { ArtworkDetailsForm, Creator } from '../useArtworkDetailsForm';

interface Props {
  form: ArtworkDetailsForm;
}

/**
 * Creator(s) section.
 *
 * - Catalog owner is the primary creator and is NOT editable: their name
 *   is shown as a small read-only label populated from `ownerDisplayName`.
 * - Co-creators are added as a flat list (one input per co-creator, with
 *   add/remove). Mirrors the legacy `DescribeTab` exactly — no "as
 *   written on the object" box on the owner.
 * - Commercial-tier accounts (no owner-creator concept) treat all entries
 *   as artists.
 */
export const CreatorsSection = ({ form }: Props) => {
  const { creators, setCreators, isCommercial, ownerDisplayName } = form;

  const ownerCreator: Creator | null = isCommercial
    ? null
    : creators.find((c) => c.isCurrentUser) ?? {
        name: getArtistDisplayName(ownerDisplayName),
        isCurrentUser: true,
      };
  const coCreators = isCommercial
    ? creators
    : creators.filter((c) => !c.isCurrentUser);

  const handleListChange = (values: string[]) => {
    if (isCommercial) {
      setCreators(values.map((name) => ({ name, isCurrentUser: false })));
    } else {
      const newCo = values.map((name) => ({ name, isCurrentUser: false }));
      setCreators(ownerCreator ? [ownerCreator, ...newCo] : newCo);
    }
  };

  const ownerNameForSummary =
    ownerCreator?.name?.trim() || ownerDisplayName?.trim() || '';
  const summary = isCommercial
    ? creators.map((c) => c.name?.trim()).filter((n): n is string => !!n).join(', ')
    : [ownerNameForSummary, ...coCreators.map((c) => c.name?.trim())]
        .filter((n): n is string => !!n)
        .join(', ');

  return (
    <Acc
      id="sec-creators"
      title="Creator(s)"
      summary={summary}
      emptyPlaceholder="No creators added"
    >
      {!isCommercial && ownerCreator && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 16,
              color:
                ownerCreator.name || ownerDisplayName
                  ? 'var(--ink-900)'
                  : 'var(--slate-500)',
              fontStyle:
                ownerCreator.name || ownerDisplayName ? 'normal' : 'italic',
            }}
          >
            {ownerCreator.name ||
              ownerDisplayName ||
              'Display name not set — update in profile settings'}
          </div>
        </div>
      )}

      <DynamicTextFields
        label={isCommercial ? 'Artist(s)' : 'Co-creator(s)'}
        addButtonText={isCommercial ? '+ Add another artist' : '+ Add co-creator'}
        initialValues={coCreators.map((c) => c.name)}
        onChange={handleListChange}
        variant="design"
      />
    </Acc>
  );
};
